/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Component for Void Claim Button
 */
import { Button, ButtonProps, Modal, Input, Form } from 'antd';
import { useEffect, useState } from 'react';
import _ from 'lodash';

import useVoidClaim from 'hooks/claim/mutate/useVoidClaim';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { CLAIM_STATUS_IDENTIFIER_CODE_MAPPING } from 'app-constants/claims';

export interface IProps extends ButtonProps {
	claimId: string;
	claimData: ClaimRequest.IClaim;
	paymentAllocationData: ClaimStatusResponse.IClaimPaymentReconciliation;
}

const findPayerIcnIdentifier = (claim835?: AnyRecord) =>
	_.find(claim835?.identifier, (identifier) =>
		_.some(
			identifier?.type?.coding,
			(coding) =>
				coding?.code === CLAIM_STATUS_IDENTIFIER_CODE_MAPPING.claimControlNumber[0].code &&
				coding?.system === fhirSystemMapping['REF-01'],
		),
	);

const VoidClaim: React.FC<IProps> = ({ claimId, claimData, paymentAllocationData, ...props }) => {
	const [open, setOpen] = useState(false);
	const [icn, setIcn] = useState('');
	const { mutateAsync: voidClaim, isPending } = useVoidClaim();

	useEffect(() => {
		const payerIcn = findPayerIcnIdentifier(paymentAllocationData?.data?.[0]?.fhirClaimResponse);
		const icnValue = payerIcn?.value || '';
		setIcn(icnValue);
	}, [claimData]);

	const handleOk = async () => {
		const voidClaimFhirResource = _.cloneDeep(claimData?.fhirResource);
		const payerIcnIdentifier = findPayerIcnIdentifier(voidClaimFhirResource?.identifier);
		const frequencyCodeExtension = _.find(
			voidClaimFhirResource?.extension,
			(extension) => extension?.url === fhirSystemMapping.frequencyCode,
		);
		if (frequencyCodeExtension) {
			frequencyCodeExtension.valueCode = '8';
		} else {
			if (!voidClaimFhirResource.extension) {
				voidClaimFhirResource.extension = [];
			}
			voidClaimFhirResource.extension.push({
				url: fhirSystemMapping.frequencyCode,
				valueCode: '8',
			});
		}

		if (payerIcnIdentifier) {
			payerIcnIdentifier.value = icn;
		} else {
			if (!voidClaimFhirResource.identifier) {
				voidClaimFhirResource.identifier = [];
			}
			voidClaimFhirResource.identifier.push({
				system: fhirSystemMapping['REF-01'],
				type: {
					coding: [
						{ code: CLAIM_STATUS_IDENTIFIER_CODE_MAPPING.claimControlNumber[0].code, system: fhirSystemMapping['REF-01'] },
					],
				},
				value: icn,
			});
		}
		await voidClaim({
			voidRequestModel: { claimId, claimVoidRequest: { claim: voidClaimFhirResource, entities: claimData?.entities } },
		});
		setOpen(false);
	};

	return (
		<>
			<Button danger onClick={() => setOpen(true)} {...props}>
				{'Void Claim'}
			</Button>
			<Modal
				title='Void Claim'
				open={open}
				onCancel={() => setOpen(false)}
				onOk={handleOk}
				okText='Confirm and Void'
				okButtonProps={{ danger: true, loading: isPending, disabled: !_.trim(icn) }}
			>
				<p className='mb-3'>
					You’re about to submit a void request for this claim. This will cancel the claim with the payer and cannot be undone.
				</p>
				<p>Enter/update the payer’s ICN to continue.</p>
				<Form.Item label="Payer's ICN" required>
					<Input value={icn} onChange={(e) => setIcn(e.target.value)} placeholder="Enter Payer's ICN" />
				</Form.Item>
			</Modal>
		</>
	);
};

export default VoidClaim;
